import dayjs from "dayjs";
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { CategoryChips } from "./CategoryChips";
import { DeleteQueryModal } from "./DeleteQueryModal";
import { StatusBadge } from "./StatusBadge";
import {
  PlacesQuery,
  PlacesQuerySearchParams,
  PlacesQuerySortField,
  PlacesQueryStatus,
} from "../../../../models/PlacesQueryData";
import ColumnFilterPopover from "../../../Common/ColumnFilterPopover";
import ConfigurableTableLayout from "../../../Common/ConfigurableTableLayout";
import InputBox from "../../../FormElement/InputBox";
import { usePlacesQueriesList } from "../hooks";

import "./styles.scss";

const STATUSES: PlacesQueryStatus[] = ["pending", "completed", "failed"];

export interface IQueriesListProps {
  onSelectQuery: (id: string) => void;
}

type QueryColumnId =
  | "name"
  | "area"
  | "category"
  | "status"
  | "results_count"
  | "owner_registered"
  | "check"
  | "created_at"
  | "actions";

const ALL_COLUMNS: QueryColumnId[] = [
  "name",
  "area",
  "category",
  "status",
  "results_count",
  "owner_registered",
  "check",
  "created_at",
  "actions",
];

const DEFAULT_VISIBLE_COLUMNS: QueryColumnId[] = [...ALL_COLUMNS];
const VISIBLE_COLUMNS_STORAGE_KEY = "GooglePlacesQueriesList_VisibleColumns";

const COLUMN_WIDTHS: Record<QueryColumnId, string> = {
  name: "260px",
  area: "140px",
  category: "220px",
  status: "150px",
  results_count: "110px",
  owner_registered: "170px",
  check: "150px",
  created_at: "190px",
  actions: "150px",
};

const normalizeVisibleColumns = (value: unknown): QueryColumnId[] => {
  if (!Array.isArray(value)) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const filtered = value.filter((col): col is QueryColumnId =>
    ALL_COLUMNS.includes(col as QueryColumnId)
  );
  const unique = Array.from(new Set(filtered));

  if (unique.length === 0) {
    return DEFAULT_VISIBLE_COLUMNS;
  }

  const required = new Set<QueryColumnId>(["name", "actions"]);
  unique.forEach((item) => required.add(item));

  return ALL_COLUMNS.filter((item) => required.has(item));
};

export const QueriesList: React.FunctionComponent<IQueriesListProps> = ({
  onSelectQuery,
}) => {
  const { t: _t } = useTranslation();
  const t = (key: string) =>
    _t(`internalToolsHomePage.gbp_places_query.list.${key}`);
  const tTable = (key: string) =>
    _t(`internalToolsHomePage.gbp_places_query.list.table.${key}`);
  const tFilter = (key: string) =>
    _t(`internalToolsHomePage.gbp_places_query.list.filter.${key}`);
  const tStatus = (key: string) =>
    _t(`internalToolsHomePage.gbp_places_query.status.${key}`);

  const {
    query,
    searchParams,
    setSearchParams,
    filterFormData,
    handleChangeFilterFormData,
  } = usePlacesQueriesList();

  const [deleteTarget, setDeleteTarget] = useState<PlacesQuery | undefined>();
  const [visibleColumns, setVisibleColumns] = useState<QueryColumnId[]>(() => {
    const savedColumns = localStorage.getItem(VISIBLE_COLUMNS_STORAGE_KEY);
    if (!savedColumns) {
      return DEFAULT_VISIBLE_COLUMNS;
    }

    try {
      return normalizeVisibleColumns(JSON.parse(savedColumns));
    } catch {
      return DEFAULT_VISIBLE_COLUMNS;
    }
  });
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const [openFilterColumnId, setOpenFilterColumnId] =
    useState<QueryColumnId | null>(null);
  const [nameInput, setNameInput] = useState(searchParams.name ?? "");

  useEffect(() => {
    setNameInput(searchParams.name ?? "");
  }, [searchParams.name]);

  const toggleFilter = (columnId: QueryColumnId) => {
    setOpenFilterColumnId((cur) => (cur === columnId ? null : columnId));
  };

  const statusOptions: { value: PlacesQueryStatus | ""; label: string }[] = [
    { value: "", label: tFilter("status_all") },
    ...STATUSES.map((status) => ({ value: status, label: tStatus(status) })),
  ];

  const handleApplyName = () => {
    setSearchParams((sp) => ({
      ...sp,
      name: nameInput.trim() || undefined,
      offset: 0,
    }));
    setOpenFilterColumnId(null);
  };

  const handleClearName = () => {
    setNameInput("");
    setSearchParams((sp) => ({ ...sp, name: undefined, offset: 0 }));
    setOpenFilterColumnId(null);
  };

  const handleChangeStatus = (status: PlacesQueryStatus | "") => {
    setSearchParams((sp) => ({
      ...sp,
      status: status || undefined,
      offset: 0,
    }));
    setOpenFilterColumnId(null);
  };

  const hasActiveNameFilter = (searchParams.name ?? "").trim() !== "";

  const hasActiveFilters =
    hasActiveNameFilter ||
    !!searchParams.status ||
    searchParams.sort !== "created_at" ||
    searchParams.order !== "desc";

  const handleClearAllFilters = () => {
    setNameInput("");
    setSearchParams({
      sort: "created_at",
      order: "desc",
      limit: searchParams.limit,
      offset: 0,
      seek_total_count: true,
    });
    setOpenFilterColumnId(null);
  };

  const items = query.data?.data ?? [];

  const renderArea = (item: PlacesQuery) => {
    if (!item.prefecture) {
      return "";
    }
    if (item.city) {
      return `${item.prefecture.name} ${item.city.name}`;
    }
    return `${item.prefecture.name}（${tTable("area_whole_prefecture")}）`;
  };

  const onSelectSortField = (field: PlacesQuerySortField) => {
    setSearchParams((sp: PlacesQuerySearchParams) => {
      if (sp.sort === field) {
        return { ...sp, order: sp.order === "asc" ? "desc" : "asc" };
      }
      return { ...sp, sort: field, order: "asc" };
    });
  };

  const toggleColumnVisibility = (columnId: QueryColumnId) => {
    if (columnId === "name" || columnId === "actions") {
      return;
    }

    setVisibleColumns((prev) => {
      const next = prev.includes(columnId)
        ? normalizeVisibleColumns(prev.filter((col) => col !== columnId))
        : normalizeVisibleColumns([...prev, columnId]);

      localStorage.setItem(VISIBLE_COLUMNS_STORAGE_KEY, JSON.stringify(next));
      return next;
    });
  };

  const getColumnLabelText = (columnId: QueryColumnId) => {
    switch (columnId) {
      case "name":
        return tTable("header.name.default");
      case "area":
        return tTable("area");
      case "category":
        return tTable("category");
      case "status":
        return tTable("header.status.default");
      case "results_count":
        return tTable("results_count");
      case "owner_registered":
        return tTable("owner_registered");
      case "check":
        return tTable("check");
      case "created_at":
        return tTable("header.created_at.default");
      case "actions":
        return tTable("delete");
    }
  };

  const getDynamicGridTemplateStyle = () => ({
    gridTemplateColumns: visibleColumns
      .map((col) => COLUMN_WIDTHS[col])
      .join(" "),
  });

  const createSortButton = (
    field: PlacesQuerySortField,
    translationKey: string
  ) => {
    const isActive = searchParams.sort === field;
    const order = searchParams.order ?? "asc";
    const label = tTable(`header.${translationKey}.default`);

    return (
      <button
        type="button"
        className={`refactored-sort-button ${isActive ? "active" : ""}`}
        onClick={() => onSelectSortField(field)}
      >
        <span>{label}</span>
        <span
          className={`sort-icon-wrap ${isActive ? "is-active" : "is-idle"}`}
          aria-hidden="true"
        >
          {isActive ? (
            order === "desc" ? (
              <ChevronDown className="sort-icon" size={14} strokeWidth={2} />
            ) : (
              <ChevronUp className="sort-icon" size={14} strokeWidth={2} />
            )
          ) : (
            <ChevronsUpDown className="sort-icon" size={14} strokeWidth={2} />
          )}
        </span>
      </button>
    );
  };

  return (
    <section className="white-border-panel padding32 gbp-places-query-list">
      <ConfigurableTableLayout
        className="gbp-places-query-table-layout"
        onContainerClick={() => {
          setShowColumnSettings(false);
          setOpenFilterColumnId(null);
        }}
        onClearAllConditions={
          hasActiveFilters ? handleClearAllFilters : undefined
        }
        clearAllLabel={tFilter("clear_all")}
        controls={
          <div
            className="column-settings-container"
            onClick={(event) => event.stopPropagation()}
          >
            <button
              type="button"
              className="btn-column-settings"
              onClick={() => setShowColumnSettings((prev) => !prev)}
            >
              {tTable("column_settings")} ⚙️
            </button>
            {showColumnSettings && (
              <div className="column-settings-dropdown">
                <div className="dropdown-title">
                  {tTable("column_settings_title")}
                </div>
                {ALL_COLUMNS.map((colId) => (
                  <label key={`setting-${colId}`} className="setting-label">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(colId)}
                      disabled={colId === "name" || colId === "actions"}
                      onChange={() => toggleColumnVisibility(colId)}
                    />
                    <span className="checkbox-custom"></span>
                    <span className="label-text">
                      {getColumnLabelText(colId)}
                    </span>
                  </label>
                ))}
              </div>
            )}
          </div>
        }
        tableContent={
          <div className="top-scrollbar">
            <div
              className="table-data table-data-grid gbp-places-query-table scrollable"
              style={getDynamicGridTemplateStyle()}
            >
              <div className="row-th refactored-facility-list-header">
                {visibleColumns.includes("name") && (
                  <div className="col-th column-name">
                    <div className="spacing header-cell-inner is-name">
                      {createSortButton("name", "name")}
                      <ColumnFilterPopover
                        isActive={hasActiveNameFilter}
                        isOpen={openFilterColumnId === "name"}
                        onToggle={() => toggleFilter("name")}
                        onRequestClose={() => setOpenFilterColumnId(null)}
                        ariaLabel={tFilter("search_placeholder")}
                      >
                        <InputBox
                          isSearch
                          placeholder={tFilter("search_placeholder")}
                          value={nameInput}
                          onChange={setNameInput}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              handleApplyName();
                            }
                          }}
                        />
                        <div className="common-column-filter-actions">
                          <button
                            type="button"
                            className="btn btn-border"
                            onClick={handleApplyName}
                          >
                            {tFilter("apply")}
                          </button>
                          {hasActiveNameFilter && (
                            <button
                              type="button"
                              className="btn btn-border btn-border-red"
                              onClick={handleClearName}
                            >
                              {tFilter("clear")}
                            </button>
                          )}
                        </div>
                      </ColumnFilterPopover>
                    </div>
                  </div>
                )}
                {visibleColumns.includes("area") && (
                  <div className="col-th column-area center">
                    <div className="spacing header-cell-inner">
                      <span className="column-label-text">
                        {tTable("area")}
                      </span>
                    </div>
                  </div>
                )}
                {visibleColumns.includes("category") && (
                  <div className="col-th column-category center">
                    <div className="spacing header-cell-inner">
                      <span className="column-label-text">
                        {tTable("category")}
                      </span>
                    </div>
                  </div>
                )}
                {visibleColumns.includes("status") && (
                  <div className="col-th column-status center">
                    <div className="spacing header-cell-inner">
                      {createSortButton("status", "status")}
                      <ColumnFilterPopover
                        isActive={!!searchParams.status}
                        isOpen={openFilterColumnId === "status"}
                        onToggle={() => toggleFilter("status")}
                        onRequestClose={() => setOpenFilterColumnId(null)}
                        ariaLabel={tFilter("status_all")}
                      >
                        <div className="common-column-filter-options">
                          {statusOptions.map((option) => (
                            <button
                              key={option.value || "all"}
                              type="button"
                              className={`common-column-filter-option ${
                                (searchParams.status ?? "") === option.value
                                  ? "selected"
                                  : ""
                              }`}
                              onClick={() => handleChangeStatus(option.value)}
                            >
                              {option.label}
                            </button>
                          ))}
                        </div>
                      </ColumnFilterPopover>
                    </div>
                  </div>
                )}
                {visibleColumns.includes("results_count") && (
                  <div className="col-th column-results_count center">
                    <div className="spacing header-cell-inner">
                      <span className="column-label-text">
                        {tTable("results_count")}
                      </span>
                    </div>
                  </div>
                )}
                {visibleColumns.includes("owner_registered") && (
                  <div className="col-th column-owner_registered center">
                    <div className="spacing header-cell-inner">
                      <span className="column-label-text coming-soon">
                        {tTable("owner_registered")}
                      </span>
                    </div>
                  </div>
                )}
                {visibleColumns.includes("check") && (
                  <div className="col-th column-check center">
                    <div className="spacing header-cell-inner">
                      <span className="column-label-text coming-soon">
                        {tTable("check")}
                      </span>
                    </div>
                  </div>
                )}
                {visibleColumns.includes("created_at") && (
                  <div className="col-th column-created_at center">
                    <div className="spacing header-cell-inner">
                      {createSortButton("created_at", "created_at")}
                    </div>
                  </div>
                )}
                {visibleColumns.includes("actions") && (
                  <div className="col-th column-actions center" />
                )}
              </div>
              {items.length === 0 ? (
                <div className="row-td text-center sub-txt">{t("empty")}</div>
              ) : (
                items.map((item) => (
                  <div
                    className="row-td gbp-places-query-row"
                    key={item.id}
                    onClick={() => onSelectQuery(item.id)}
                  >
                    {visibleColumns.includes("name") && (
                      <div className="col-td column-name">
                        <div className="spacing">
                          <span className="cell-ellipsis" title={item.name}>
                            {item.name}
                          </span>
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("area") && (
                      <div className="col-td center column-area">
                        <div className="spacing">
                          <span
                            className="cell-ellipsis"
                            title={renderArea(item)}
                          >
                            {renderArea(item)}
                          </span>
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("category") && (
                      <div className="col-td center column-category">
                        <div className="spacing">
                          <CategoryChips categories={item.categories} />
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("status") && (
                      <div className="col-td center column-status">
                        <div className="spacing">
                          <StatusBadge
                            status={item.status}
                            errorMessage={item.error_message}
                          />
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("results_count") && (
                      <div className="col-td center column-results_count">
                        <div className="spacing">
                          {item._count?.results ?? 0}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("owner_registered") && (
                      <div className="col-td center column-owner_registered">
                        <div className="spacing txt coming-soon">
                          {_t(
                            "internalToolsHomePage.gbp_places_query.stats.coming_soon"
                          )}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("check") && (
                      <div className="col-td center column-check">
                        <div className="spacing txt coming-soon">
                          {_t(
                            "internalToolsHomePage.gbp_places_query.stats.coming_soon"
                          )}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("created_at") && (
                      <div className="col-td center column-created_at">
                        <div className="spacing">
                          {dayjs(item.created_at).format("YYYY/MM/DD HH:mm")}
                        </div>
                      </div>
                    )}
                    {visibleColumns.includes("actions") && (
                      <div className="col-td center buttons-section column-actions">
                        <button
                          type="button"
                          className="btn btn-border btn-border-red"
                          onClick={(e) => {
                            e.stopPropagation();
                            setDeleteTarget(item);
                          }}
                        >
                          {tTable("delete")}
                        </button>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        }
        pagination={{
          filterFormData,
          totalRowsNumber: query.data?.count ?? 0,
          onChangeFilterFormData: handleChangeFilterFormData,
        }}
      />
      {deleteTarget && (
        <DeleteQueryModal
          query={deleteTarget}
          onClose={() => setDeleteTarget(undefined)}
        />
      )}
    </section>
  );
};

export default QueriesList;
